import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';

import { SubText } from 'components/Toolkit/Inputs/SubText';
import { ErrorMessage } from 'components/Toolkit/Inputs/ErrorMessage';
import { HelpText } from 'components/Toolkit/Inputs/HelpText';
import { CharacterCount } from 'components/Toolkit/Inputs/CharacterCount';
import { Label } from 'components/Toolkit/Inputs/Label';

import { space, ISpace } from 'helpers/genericStyles';

export interface TextAreaProps extends ISpace {
  /** Allow styled-components to style from parent */
  className?: string;
  /** Hint text to display */
  placeholder?: string;
  /** Initial value for the textarea */
  value?: string;
  /** Additional hint text to display */
  helpText?: string;
  /** Label for the textarea */
  label?: string;
  /** Disable the textarea */
  disabled?: boolean;
  /** Disable editing of the textarea */
  readOnly?: boolean;
  /** Automatically focus the textarea */
  autoFocus?: boolean;
  /** Force the focus state on the textarea */
  focused?: boolean;
  /** Does the textarea have an error */
  hasError?: boolean;
  /** Error message to display beneath the textarea */
  errorMessage?: string;
  /** Name of the textarea */
  name?: string;
  /** ID for the textarea */
  id?: string;
  /** Defines a specific role attribute for the textarea */
  role?: string;
  /** Enable automatic completion by the browser */
  autoComplete?: 'on' | 'off';
  /** Maximum character length for an textarea */
  maxLength?: number;
  /** Minimum character length for an textarea */
  minLength?: number;
  /** Number of rows of textarea */
  rows?: number;
  /** Indicates whether or not the character count should be displayed */
  showCharacterCount?: boolean;
  /** Enable (default) or disable resizing. */
  resize?: boolean;
  /** Callback when value is changed */
  onChange?(value: string): void;
  /** Callback when textarea is focused */
  onFocus?(): void;
  /** Callback when focus is removed */
  onBlur?(): void;
  /** Callback on keypress */
  onKeyPress?(event: React.KeyboardEvent): void;
}

export const Inputs = styled.textarea<{
  hasError: boolean;
  resize: boolean;
}>`
  ${({ theme }) => theme.fontSize.M16};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  border: 1px solid ${({ theme }) => theme.colors.GREY};
  ${({ theme, hasError }) =>
    hasError &&
    `border-color: ${theme.colors.RED_DARK}; color: ${theme.colors.RED_DARK};`}
  padding: ${({ theme }) => `${theme.spacing.S8} ${theme.spacing.M16}`};
  background-color: ${({ theme }) => theme.colors.WHITE};
  width: 100%;
  color: ${({ theme }) => theme.colors.GREY_DARKER};
  resize: ${({ resize, disabled }) =>
    resize && !disabled ? 'vertical' : 'none'};
  min-height: ${({ resize }) => (resize ? '114px' : '0')};
  margin-bottom: ${({ theme }) => theme.spacing.S4};

  &:focus:not(.focus-visible) {
    outline: none;
  }

  &.focus-visible {
    border-width: 1px;
    border-style: solid;
    outline: none;
    border-color: ${({ theme }) => theme.colors.BLUE};
  }

  &::-webkit-input-placeholder {
    color: ${({ theme, hasError }) =>
      hasError ? theme.colors.RED_DARK : theme.colors.GREY};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.GREY_LIGHTER};
    cursor: not-allowed;
  }

  &::-webkit-input-placeholder {
    color: ${({ theme, hasError }) =>
      hasError ? theme.colors.RED_DARK : theme.colors.GREY};
  }
`;

const Container = styled.div`
  ${space};
`;

const SSubText = styled(SubText)`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.S8};
`;

const SCharacterCount = styled(CharacterCount)`
  margin-left: auto;
  white-space: nowrap;
`;

function TextArea({
  placeholder,
  value = '',
  rows = 4,
  helpText,
  label,
  disabled,
  readOnly,
  autoFocus,
  focused,
  hasError = false,
  errorMessage,
  name,
  id,
  role,
  autoComplete,
  maxLength,
  minLength,
  resize = true,
  onChange,
  onFocus,
  onBlur,
  onKeyPress,
  showCharacterCount,
  className,
  ...spaceProps
}: TextAreaProps) {
  const alwaysSetId = id ? id : name;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [characterCount, setCharacterCount] = useState<number>(
    value?.length || 0,
  );
  const isError = hasError || Boolean(errorMessage && errorMessage.length > 0);

  function handleChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    const val = event.currentTarget.value;
    if (showCharacterCount) {
      setCharacterCount(val.length);
    }
    onChange && onChange(val);
  }

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea || focused === undefined) return;
    focused ? textarea.focus() : textarea.blur();
  }, [focused]);

  useEffect(() => {
    if (value.length === 0) {
      setCharacterCount(0);
    }
  }, [value]);

  return (
    <Container className={className} {...spaceProps}>
      {label && <Label htmlFor={alwaysSetId}>{label}</Label>}
      <Inputs
        name={name}
        id={alwaysSetId}
        disabled={disabled}
        readOnly={readOnly}
        role={role}
        autoFocus={autoFocus}
        value={value}
        placeholder={placeholder}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyPress={onKeyPress}
        autoComplete={autoComplete}
        onChange={handleChange}
        minLength={minLength}
        maxLength={maxLength}
        hasError={isError}
        rows={rows}
        data-testid="view-textarea"
        resize={resize}
        ref={textareaRef}
      />
      <SSubText>
        {errorMessage ? (
          <ErrorMessage text={errorMessage} />
        ) : (
          helpText && <HelpText text={helpText} />
        )}
        {Boolean(showCharacterCount && maxLength && !helpText) && (
          <SCharacterCount count={characterCount} maxLength={maxLength!} />
        )}
      </SSubText>
    </Container>
  );
}

export { TextArea };
